import {
  Briefcase,
  Home,
  Layers,
  Menu,
  NotebookTabsIcon,
  User,
  Users,
  Shield,
  LogOut,
  ChevronDown,
  Wallet,
  Sparkles,
  ExternalLink
} from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useWallet } from '../../context/WalletContext';
import NetworkSwitcher from '../common/NetworkSwitcher';
import { useContractServices } from '../../context/ContractServicesContext';

function Navbar() {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const { isConnected, walletAddress, innovatorProfile, connectWallet, loading, disconnectWallet } = useWallet();
  const { services, isReady } = useContractServices();
  const name = innovatorProfile?.name || 'User';
  const profileMenuRef = useRef(null);

  // Check if the connected wallet is an admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (isConnected && walletAddress && isReady && services.admin) {
        try {
          const adminStatus = await services.admin.isAdmin(walletAddress);
          setIsAdmin(adminStatus);
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    };
    checkAdminStatus();
  }, [walletAddress, isConnected, isReady, services]);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogo = () => {
    navigate('/');
  };

  const formatAddress = (address) => {
    return `${address.substring(0, 2)}...${address.substring(address.length - 2)}`;
  };

  const handleProfileClick = () => {
    navigate('/profile');
    setShowProfileMenu(false);
  };

  const activeClassName = 'border-b-2 border-indigo-600';

  return (
    <div className="bg-white text-gray-800 shadow-sm border-b border-gray-100 sticky top-0 z-50">
      <div className="flex items-center justify-between px-4 lg:px-8 py-3">
        {/* LEFT: Logo */}
        <div
          className="flex items-center flex-none cursor-pointer hover:opacity-80 transition-opacity"
          onClick={handleLogo}
        >
          <h1 className="text-lg sm:text-xl font-bold text-blue-800">CelystikHub</h1>
          <span className="ml-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-[8px] px-1.5 py-0.5 rounded-full text-white">
            BETA
          </span>
        </div>
       

        {/* CENTER: Navigation - Hidden on mobile */}
        <div className="hidden lg:flex flex-1 justify-center">
          <ul className="flex space-x-6 items-center">
            <li>
              <NavLink to="/" className={({ isActive }) =>
                `flex items-center gap-1.5 text-sm text-gray-700 hover:text-indigo-600 transition-all duration-300 hover:scale-105 ${isActive ? `text-indigo-600 font-semibold ${activeClassName}` : ''}`}>
                <Home size={16} /> Home
              </NavLink>
            </li>
            <li>
              <NavLink to="/projects" className={({ isActive }) =>
                `flex items-center gap-1.5 text-sm text-gray-700 hover:text-indigo-600 transition-all duration-300 hover:scale-105 ${isActive ? `text-indigo-600 font-semibold ${activeClassName}` : ''}`}>
                <Layers size={16} /> Projects
              </NavLink>
            </li>
            <li>
              <NavLink to="/innovators" className={({ isActive }) =>
                `flex items-center gap-1.5 text-sm text-gray-700 hover:text-indigo-600 transition-all duration-300 hover:scale-105 ${isActive ? `text-indigo-600 font-semibold ${activeClassName}` : ''}`}>
                <Users size={16} /> Innovators
              </NavLink>
            </li>
            <li>
              <NavLink to="/portfolio" className={({ isActive }) =>
                `flex items-center gap-1.5 text-sm text-gray-700 hover:text-indigo-600 transition-all duration-300 hover:scale-105 ${isActive ? `text-indigo-600 font-semibold ${activeClassName}` : ''}`}>
                <Briefcase size={16} /> Portfolio
              </NavLink>
            </li>
            {isAdmin && (
              <li>
                <NavLink to="/admin" className={({ isActive }) =>
                  `flex items-center gap-1.5 text-sm text-gray-700 hover:text-indigo-600 transition-all duration-300 hover:scale-105 ${isActive ? `text-indigo-600 font-semibold ${activeClassName}` : ''}`}>
                  <Shield size={16} /> Admin
                </NavLink>
              </li>
            )}
          </ul>
        </div>

        {/* RIGHT: NetworkSwitcher + Wallet */}
        <div className="flex items-center space-x-2">
  {isConnected && (
    <div className="flex items-center space-x-4">
      <a
        href="https://celystikhub.gitbook.io/www.celystikhub-gitbook.com"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center space-x-1 sm:space-x-2 px-2 py-1 sm:px-3 sm:py-2 rounded-md sm:rounded-lg bg-blue-600 text-white text-xs sm:text-sm transition-all duration-300 hover:scale-105"
      >
        <span>Doc</span>
        <ExternalLink size={14} className="sm:size-4" />
      </a>
      

      <div className="hidden sm:block mr-1">
        <NetworkSwitcher />
      </div>
    </div>



          )}

          {isConnected ? (
            <>
              {/* Wallet Connection Button */}
              <div className="relative" ref={profileMenuRef}>
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center space-x-2 bg-gradient-to-r from-indigo-600 to-purple-600 px-2 sm:px-3 py-1.5 rounded-lg hover:shadow-lg transition-all duration-300 text-white text-sm group relative overflow-hidden"
                >
                  <div className="flex items-center space-x-2">
                    <div className="relative">
                      <Wallet size={16} className="relative z-10" />
                      <Sparkles size={12} className="absolute -top-1 -right-1 text-yellow-300 animate-pulse" />
                    </div>
                    <span className="hidden sm:inline font-mono tracking-wide bg-white/10 px-2 py-0.5 rounded-md">{formatAddress(walletAddress)}</span>
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <div className="h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse"></div>
                    <ChevronDown size={14} className={`transition-transform duration-300 ${showProfileMenu ? 'rotate-180' : ''}`} />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                          <User size={16} className="text-indigo-600" />
                        </div>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                </button>

                {showProfileMenu && (
                  <div className="absolute right-0 mt-2 w-56 rounded-lg shadow-lg bg-white text-gray-800 z-50 border border-gray-100 animate-fadeIn">
                    <div className="p-3 border-b border-gray-100">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                          <User size={16} className="text-indigo-600" />
                        </div>
                        <div>
                          <button
                            onClick={() => {
                              handleProfileClick();
                              setShowProfileMenu(false);
                            }}
                            className="text-sm font-medium text-gray-900 hover:text-indigo-600 transition-colors duration-200"
                          >
                            {/* {name} */}
                            My Profile
                          </button>
                          {/* <p className="text-xs text-gray-500 font-mono mt-0.5">{formatAddress(walletAddress)}</p> */}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={disconnectWallet}
                      className="flex w-full items-center text-left px-4 py-2 text-sm hover:bg-red-50 transition-colors duration-200 text-red-600"
                    >
                      <LogOut size={14} className="mr-2" />
                      Disconnect Wallet
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-2 bg-blue-600 text-white px-2 py-1.5 rounded-lg transition-all duration-300 hover:scale-105">
                <a
                  href="https://celystikhub.gitbook.io/www.celystikhub-gitbook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2  rounded-lg transition-all duration-300 hover:scale-105"
                >
                  <span className='text-sm'>Docs</span>
                  <ExternalLink size={16} className="text-white" />
                </a>
              </div>
              <button
                onClick={connectWallet}
                disabled={loading}
                className="flex items-center space-x-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg px-2 sm:px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-300 hover:scale-105 group relative overflow-hidden"
              >
                <Wallet size={16} />
                <span className="hidden sm:inline">{loading ? 'Connecting...' : 'Connect Wallet'}</span>
                <span className="sm:hidden">{loading ? '...' : 'Connect'}</span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </button>
            </div>
          )}

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 transition-colors duration-200"
          >
            <Menu size={20} className="text-gray-700" />
          </button>
        </div>
      </div>

      {/* MOBILE NAVIGATION */}
      <div
        className={`lg:hidden fixed inset-0 bg-black/20 backdrop-blur-sm transition-opacity duration-300 z-40 ${mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        onClick={() => setMobileMenuOpen(false)}
      >
        <div
          className={`absolute right-0 top-0 h-full w-[280px] bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          onClick={e => e.stopPropagation()}
        >
          <div className="flex flex-col h-full">
            {/* Mobile Menu Header */}
            <div className="p-4 border-b border-gray-100">
              {isConnected ? (
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                    <User size={20} className="text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                    <p className="text-xs text-gray-500 font-mono truncate">{formatAddress(walletAddress)}</p>
                  </div>
                </div>
              ) : (
                <button
                  onClick={connectWallet}
                  disabled={loading}
                  className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:scale-105"
                >
                  <Wallet size={16} />
                  <span>{loading ? 'Connecting...' : 'Connect Wallet'}</span>
                </button>
              )}
            </div>

            {/* Mobile Menu Navigation */}
            <nav className="flex-1 overflow-y-auto p-4 space-y-1">
              <NavLink
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors duration-200 ${isActive
                    ? 'bg-indigo-50 text-indigo-600 font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                  }`
                }
              >
                <Home size={18} />
                <span>Home</span>
              </NavLink>
              <NavLink
                to="/projects"
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors duration-200 ${isActive
                    ? 'bg-indigo-50 text-indigo-600 font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                  }`
                }
              >
                <Layers size={18} />
                <span>Projects</span>
              </NavLink>
              <NavLink
                to="/innovators"
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors duration-200 ${isActive
                    ? 'bg-indigo-50 text-indigo-600 font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                  }`
                }
              >
                <Users size={18} />
                <span>Innovators</span>
              </NavLink>
              <NavLink
                to="/portfolio"
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors duration-200 ${isActive
                    ? 'bg-indigo-50 text-indigo-600 font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                  }`
                }
              >
                <Briefcase size={18} />
                <span>Portfolio</span>
              </NavLink>
              {isAdmin && (
                <NavLink
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors duration-200 ${isActive
                      ? 'bg-indigo-50 text-indigo-600 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                    }`
                  }
                >
                  <Shield size={18} />
                  <span>Admin</span>
                </NavLink>
              )}
            </nav>

            {/* Mobile Menu Footer */}
            <div className="p-4 border-t border-gray-100">
              {isConnected && (
                <>
                  <div className="mb-3">
                    <NetworkSwitcher />
                  </div>
                  <button
                    onClick={() => {
                      disconnectWallet();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                  >
                    <LogOut size={16} />
                    <span>Disconnect Wallet</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = `
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes slideIn {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}

.animate-fadeIn {
  animation: fadeIn 0.2s ease-out;
}

.animate-slideIn {
  animation: slideIn 0.3s ease-out;
}
`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

export default Navbar;
